import {
    extractTemplateParams,
    OutputSerializer,
    parseMagicMd,
    type ContentDefinition,
    type ImportDocumentSnapshot,
    type ParsedDocument,
    type PluginDefinition,
    type PluginOutput,
    type TemplateInputSchema,
    type TemplateInputType,
} from "@magic-md/core";
import { RefOrValue, type RefOrValueProps, type ScopeVariable } from "@magic-md/plugin-sdk";
import { useT } from "@magic-md/i18n/react";
import { Check, ChevronDown, ChevronRight, Plus, RefreshCcw, Trash2, X } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDocumentAssetReferences } from "../hooks/useDocumentAssetReferences";
import { useDraggablePopover } from "../hooks/useDraggablePopover";
import { attachDefinitionOutputs } from "../lib/config-panel-units";
import {
    isEditorTargetProvenanceCurrent,
    resolveSerializerTargetLocator,
    type EditorSerializerTargetLocator,
} from "../lib/unit-edit-target";
import { usePaneContextOptional } from "./PaneContext";
import { useEditorStore } from "../store/editor-store";
import { getThemeAccentStyle } from "../theme/runtime/theme-accents";

const outputSerializer = new OutputSerializer();
const INPUT_TYPES: TemplateInputType[] = ["string", "number", "integer", "boolean", "array", "object"];
let draftInputFieldCounter = 0;
const EMPTY_DEFINITIONS = new Map<string, PluginDefinition>();
const EMPTY_OUTPUTS = new Map<string, PluginOutput>();
const EMPTY_CONTENT_DEFINITIONS = new Map<string, ContentDefinition>();
const EMPTY_IMPORT_DOCUMENTS = new Map<string, ImportDocumentSnapshot>();

export interface TemplateInputsTarget {
    unitKind: "define" | "content" | "for" | "use";
    unitId: string;
    x: number;
    y: number;
    tabId: string;
    paneId?: string;
    filePath?: string;
    /** Exact source occurrence captured by the CodeMirror widget. */
    locator?: EditorSerializerTargetLocator;
}

interface DraftInputField {
    key: string;
    name: string;
    type: TemplateInputType;
    title: string;
    description: string;
    defaultValue: unknown;
    enumValue: string;
    required: boolean;
}

export function TemplateInputsPopover({
    target,
    onClose,
}: {
    target: TemplateInputsTarget | null;
    onClose: () => void;
}) {
    const t = useT("editor");
    const ref = useRef<HTMLDivElement>(null);
    const paneCtx = usePaneContextOptional();
    const { onHeaderMouseDown, position } = useDraggablePopover({
        anchor: target ? { x: target.x, y: target.y } : null,
        width: 520,
        height: 460,
    });

    const tab = useEditorStore((s) => {
        const id = target?.tabId ?? s.activeTabId;
        return s.tabs.find((item) => item.id === id);
    });
    const parsedDocument = useEditorStore((s) => {
        const id = target?.tabId ?? s.activeTabId;
        return id ? s.parsedDocumentMap.get(id) ?? (id === s.activeTabId ? s.parsedDocument : null) : null;
    });
    const provenanceMatches = !target || !paneCtx || isEditorTargetProvenanceCurrent(
        target,
        {
            tabId: paneCtx.tabId,
            paneId: paneCtx.paneId,
            filePath: paneCtx.tab?.filePath,
        },
    );
    const validatedTargetLocator = useMemo(() => {
        if (!target?.locator) return undefined;
        if (!target.locator.fingerprint) return target.locator;
        if (!tab?.content || !provenanceMatches) return undefined;
        return resolveSerializerTargetLocator({
            content: tab.content,
            unitKind: target.unitKind,
            unitId: target.unitId,
            locator: target.locator,
            fallbackOccurrence: target.unitKind === "use" ? "first" : "last",
        });
    }, [provenanceMatches, tab?.content, target]);

    const unitState = useMemo(
        () => target ? readTemplateInputState(
            parsedDocument,
            target.unitKind,
            target.unitId,
            tab?.content,
            validatedTargetLocator,
        ) : { params: [], schema: undefined },
        [parsedDocument, tab?.content, target, validatedTargetLocator],
    );
    const persistedFields = useMemo(
        () => schemaToFields(unitState.schema, unitState.params),
        [unitState.schema, unitState.params],
    );

    const [fields, setFields] = useState<DraftInputField[]>(persistedFields);
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
        () => new Set(persistedFields.length <= 1 ? persistedFields.map((field) => field.key) : []),
    );
    const inputScopeVariables = useMemo(
        () => buildInputScopeVariables(fields, t),
        [fields, t],
    );
    const definitionsWithOutputs = useMemo(
        () => attachDefinitionOutputs(
            paneCtx?.definitions ?? parsedDocument?.definitions ?? EMPTY_DEFINITIONS,
            paneCtx?.definitionOutputs ?? EMPTY_OUTPUTS,
        ),
        [paneCtx?.definitions, paneCtx?.definitionOutputs, parsedDocument?.definitions],
    );
    const availableAssets = useDocumentAssetReferences(
        paneCtx?.tab?.filePath ?? tab?.filePath,
        paneCtx?.importDocuments ?? EMPTY_IMPORT_DOCUMENTS,
    );
    const refContext = useMemo<RefOrValueProps["context"]>(() => ({
        currentDefinitionId: target?.unitId,
        getAvailableDefinitions: () => definitionsWithOutputs,
        getAvailableContentDefinitions: () => paneCtx?.contentDefinitions ?? parsedDocument?.contentDefinitions ?? EMPTY_CONTENT_DEFINITIONS,
        getAvailableImportDocuments: () => paneCtx?.importDocuments ?? EMPTY_IMPORT_DOCUMENTS,
        getAvailableAssets: () => availableAssets,
        getAvailableUnits: () => [],
        scopeVariables: inputScopeVariables,
    }), [
        target?.unitId,
        definitionsWithOutputs,
        paneCtx?.contentDefinitions,
        paneCtx?.importDocuments,
        parsedDocument?.contentDefinitions,
        availableAssets,
        inputScopeVariables,
    ]);
    const targetKey = target
        ? [
            target.tabId,
            target.unitKind,
            target.unitId,
            target.locator?.startLine ?? "",
            target.locator?.endLine ?? "",
            target.locator?.occurrence ?? "",
            target.locator?.fingerprint ?? "",
            target.locator?.candidateCount ?? "",
        ].join(":")
        : null;
    const lastTargetKey = useRef(targetKey);
    useEffect(() => {
        if (targetKey !== lastTargetKey.current) {
            lastTargetKey.current = targetKey;
            setFields(persistedFields);
            setExpandedKeys(new Set(persistedFields.length <= 1 ? persistedFields.map((field) => field.key) : []));
        }
    }, [targetKey, persistedFields]);

    useEffect(() => {
        if (!target) return;
        const handler = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as HTMLElement)) onClose();
        };
        const timer = setTimeout(() => document.addEventListener("mousedown", handler), 50);
        return () => {
            clearTimeout(timer);
            document.removeEventListener("mousedown", handler);
        };
    }, [target, onClose]);

    useEffect(() => {
        if (
            target
            && (
                !provenanceMatches
                || (target.locator?.fingerprint && !validatedTargetLocator)
            )
        ) {
            onClose();
        }
    }, [onClose, provenanceMatches, target, validatedTargetLocator]);

    useEffect(() => {
        if (!target) return;
        const handler = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [target, onClose]);

    const addField = useCallback(() => {
        const field = createDraftInputField({ name: nextInputName(fields) });
        setFields((prev) => [...prev, field]);
        setExpandedKeys((prev) => new Set(prev).add(field.key));
    }, [fields]);

    const syncUsedInputs = useCallback(() => {
        setFields((prev) => {
            const seen = new Set(prev.map((field) => field.name));
            const additions = unitState.params
                .filter((param) => !seen.has(param))
                .map((param) => createDraftInputField({ name: param }));
            if (additions.length > 0) {
                setExpandedKeys((expanded) => {
                    const next = new Set(expanded);
                    for (const field of additions) next.add(field.key);
                    return next;
                });
            }
            return [...prev, ...additions];
        });
    }, [unitState.params]);

    const apply = useCallback(() => {
        if (!target || !tab) return;
        if (target.locator?.fingerprint && !validatedTargetLocator) {
            onClose();
            return;
        }
        const schema = fieldsToSchema(fields);
        const locator = resolveSerializerTargetLocator({
            content: tab.content,
            unitKind: target.unitKind,
            unitId: target.unitId,
            locator: validatedTargetLocator,
            fallbackOccurrence: target.unitKind === "use" ? "first" : "last",
        });
        if (target.locator?.fingerprint && !locator) {
            onClose();
            return;
        }
        let updated = outputSerializer.updateTemplateInputs(
            tab.content,
            target.unitKind,
            target.unitId,
            schema,
            locator,
        );
        if (schema?.properties && Object.keys(schema.properties).length > 0) {
            updated = outputSerializer.updateUnitAttr(
                updated,
                target.unitKind,
                target.unitId,
                "template",
                "true",
                locator,
            );
        }
        if (updated !== tab.content) {
            useEditorStore.getState().setContentImmediate(updated, tab.id);
        }
        onClose();
    }, [fields, onClose, tab, target, validatedTargetLocator]);

    if (!target) return null;

    const popoverPosition = position ?? { x: target.x, y: target.y + 4 };
    const undeclaredCount = unitState.params.filter(
        (param) => !fields.some((field) => field.name === param),
    ).length;

    return (
        <div
            ref={ref}
            className="fixed z-[10000] w-[480px] max-h-[72vh] overflow-hidden rounded-xl border border-border bg-background shadow-xl"
            style={{ left: popoverPosition.x, top: popoverPosition.y }}
        >
            <div
                className="flex h-10 cursor-grab select-none items-center gap-2 border-b border-border bg-muted/50 px-3 active:cursor-grabbing"
                onMouseDownCapture={onHeaderMouseDown}
                title={t("templateInputs.dragMove")}
            >
                <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                    INPUTS
                </span>
                <span
                    className="min-w-0 flex-1 truncate text-[12px] font-semibold text-foreground"
                    title={`${target.unitKind}/${target.unitId}`}
                >
                    {target.unitId}
                </span>
                <span className="shrink-0 text-[10px] text-muted-foreground/70">
                    {target.unitKind}
                </span>
                <button
                    type="button"
                    className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                    onClick={onClose}
                    aria-label={t("templateInputs.closeEditor")}
                >
                    <X size={14} />
                </button>
            </div>

            <div className="max-h-[56vh] overflow-auto p-2.5">
                {undeclaredCount > 0 ? (
                    <button
                        type="button"
                        className="mb-2 flex w-full items-center gap-2 rounded-md border border-dashed border-border/70 bg-muted/30 px-2 py-1.5 text-left text-[11px] text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
                        onClick={syncUsedInputs}
                    >
                        <RefreshCcw size={13} />
                        <span>{t("templateInputs.syncUndeclared", { count: undeclaredCount })}</span>
                    </button>
                ) : null}

                {fields.length > 0 ? (
                    <div className="overflow-hidden rounded-md border border-border/70 bg-background">
                        {fields.map((field, index) => (
                            <InputFieldRow
                                key={field.key}
                                field={field}
                                refContext={refContext}
                                expanded={expandedKeys.has(field.key)}
                                onToggle={() =>
                                    setExpandedKeys((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(field.key)) next.delete(field.key);
                                        else next.add(field.key);
                                        return next;
                                    })
                                }
                                onChange={(next) =>
                                    setFields((prev) => prev.map((item, itemIndex) => itemIndex === index ? next : item))
                                }
                                onRemove={() => {
                                    setFields((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
                                    setExpandedKeys((prev) => {
                                        const next = new Set(prev);
                                        next.delete(field.key);
                                        return next;
                                    });
                                }}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="rounded-md border border-dashed border-border px-3 py-6 text-center text-[11px] text-muted-foreground">
                        {t("templateInputs.noneDeclared")}
                    </div>
                )}
            </div>

            <div className="flex items-center justify-between border-t border-border px-3 py-2">
                <button
                    type="button"
                    className="flex items-center gap-1 rounded px-2 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    onClick={addField}
                >
                    <Plus size={13} />
                    {t("templateInputs.add")}
                </button>
                <button
                    type="button"
                    className="flex items-center gap-1 rounded px-2.5 py-1 text-[11px] font-medium theme-accent-icon-button"
                    style={getThemeAccentStyle("success")}
                    onClick={apply}
                >
                    <Check size={13} />
                    {t("common.save")}
                </button>
            </div>
        </div>
    );
}

function InputFieldRow({
    field,
    refContext,
    expanded,
    onToggle,
    onChange,
    onRemove,
}: {
    field: DraftInputField;
    refContext: RefOrValueProps["context"];
    expanded: boolean;
    onToggle: () => void;
    onChange: (field: DraftInputField) => void;
    onRemove: () => void;
}) {
    const t = useT("editor");
    const displayName = field.name.trim() || t("templateInputs.unnamedInput");
    const displayTitle = field.title.trim();
    return (
        <div className="border-b border-border/60 last:border-b-0">
            <div className="flex h-9 items-center gap-1.5 px-2">
                <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-1 text-left transition-colors hover:bg-muted/50"
                    onClick={onToggle}
                    aria-expanded={expanded}
                >
                    {expanded ? (
                        <ChevronDown size={12} className="shrink-0 text-muted-foreground/60" />
                    ) : (
                        <ChevronRight size={12} className="shrink-0 text-muted-foreground/60" />
                    )}
                    <span className="min-w-0 truncate font-mono text-[11px] font-semibold text-foreground">
                        {displayName}
                    </span>
                    {displayTitle ? (
                        <span className="min-w-0 truncate text-[10px] text-muted-foreground/70">
                            {displayTitle}
                        </span>
                    ) : null}
                </button>
                {field.required ? (
                    <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 text-[9px] font-medium text-primary">
                        {t("common.required")}
                    </span>
                ) : null}
                <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[9px] text-muted-foreground">
                    {field.type}
                </span>
                <button
                    type="button"
                    className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground/50 transition-colors hover:bg-destructive/10 hover:text-destructive"
                    onClick={onRemove}
                    aria-label={`Remove ${field.name}`}
                >
                    <Trash2 size={12} />
                </button>
            </div>

            {expanded ? (
                <div className="grid grid-cols-2 gap-2 border-t border-border/40 bg-muted/20 p-2">
                    <FieldLabel label={t("templateInputs.name")}>
                        <input
                            className="h-7 w-full rounded border border-border bg-background px-2 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                            value={field.name}
                            onChange={(event) => onChange({ ...field, name: event.target.value })}
                            placeholder="name"
                        />
                    </FieldLabel>
                    <FieldLabel label={t("templateInputs.type")}>
                        <select
                            className="h-7 w-full rounded border border-border bg-background px-2 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                            value={field.type}
                            onChange={(event) => onChange({ ...field, type: event.target.value as TemplateInputType })}
                        >
                            {INPUT_TYPES.map((type) => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </FieldLabel>
                    <FieldLabel label={t("templateInputs.displayName")}>
                        <input
                            className="h-7 w-full rounded border border-border bg-background px-2 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                            value={field.title}
                            onChange={(event) => onChange({ ...field, title: event.target.value })}
                            placeholder={t("templateInputs.displayName")}
                        />
                    </FieldLabel>
                    <DefaultValueEditor
                        field={field}
                        context={refContext}
                        onChange={onChange}
                    />
                    <FieldLabel label={t("templateInputs.description")}>
                        <input
                            className="h-7 w-full rounded border border-border bg-background px-2 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                            value={field.description}
                            onChange={(event) => onChange({ ...field, description: event.target.value })}
                            placeholder={t("templateInputs.description")}
                        />
                    </FieldLabel>
                    <div className="grid grid-cols-[1fr_auto] gap-2">
                        <FieldLabel label={t("templateInputs.enum")}>
                            <input
                                className="h-7 w-full rounded border border-border bg-background px-2 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50"
                                value={field.enumValue}
                                onChange={(event) => onChange({ ...field, enumValue: event.target.value })}
                                placeholder={t("templateInputs.commaSeparated")}
                            />
                        </FieldLabel>
                        <label className="flex items-end gap-1 pb-1 text-[10px] text-muted-foreground">
                            <input
                                type="checkbox"
                                checked={field.required}
                                onChange={(event) => onChange({ ...field, required: event.target.checked })}
                            />
                            {t("common.required")}
                        </label>
                    </div>
                </div>
            ) : null}
        </div>
    );
}

function FieldLabel({
    label,
    children,
}: {
    label: string;
    children: ReactNode;
}) {
    return (
        <label className="space-y-1">
            <span className="block text-[9px] font-medium text-muted-foreground/70">
                {label}
            </span>
            {children}
        </label>
    );
}

function DefaultValueEditor({
    field,
    context,
    onChange,
}: {
    field: DraftInputField;
    context: RefOrValueProps["context"];
    onChange: (field: DraftInputField) => void;
}) {
    const t = useT("editor");
    const config = useMemo<Record<string, unknown>>(
        () => ({ default: field.defaultValue }),
        [field.defaultValue],
    );
    const handleDefaultChange = useCallback(
        (nextConfig: Record<string, unknown>) => {
            onChange({ ...field, defaultValue: nextConfig.default });
        },
        [field, onChange],
    );

    return (
        <div className="space-y-1">
            <span className="block text-[9px] font-medium text-muted-foreground/70">
                {t("templateInputs.defaultValue")}
            </span>
            <RefOrValue
                label=""
                refField="default"
                manualFields={[]}
                config={config}
                onChange={handleDefaultChange}
                context={context}
                borderless
                inline
            >
                {renderDefaultControl(field, field.defaultValue, (value) => {
                    onChange({ ...field, defaultValue: value });
                }, t)}
            </RefOrValue>
        </div>
    );
}

function readTemplateInputState(
    parsed: ParsedDocument | null,
    unitKind: TemplateInputsTarget["unitKind"],
    unitId: string,
    sourceContent?: string,
    locator?: EditorSerializerTargetLocator,
): { params: string[]; schema?: TemplateInputSchema } {
    if (sourceContent && locator) {
        const locatedSource = readTemplateInputStateFromSource(
            sourceContent,
            unitKind,
            unitId,
            locator,
        );
        if (locatedSource) return locatedSource;
    }

    const fromParsed = readParsedTemplateInputState(parsed, unitKind, unitId);
    if (fromParsed.found) return { params: fromParsed.params, schema: fromParsed.schema };

    const fromSource = sourceContent
        ? readTemplateInputStateFromSource(sourceContent, unitKind, unitId)
        : null;
    if (fromSource) return fromSource;

    return { params: [] };
}

function readParsedTemplateInputState(
    parsed: ParsedDocument | null,
    unitKind: TemplateInputsTarget["unitKind"],
    unitId: string,
): { found: boolean; params: string[]; schema?: TemplateInputSchema } {
    if (!parsed) return { found: false, params: [] };
    if (unitKind === "define") {
        const def = parsed.definitions.get(unitId);
        return { found: !!def, params: def?.params ?? [], schema: def?.inputSchema };
    }
    if (unitKind === "content") {
        const def = parsed.contentDefinitions?.get(unitId);
        return { found: !!def, params: def?.params ?? [], schema: def?.inputSchema };
    }
    if (unitKind === "for") {
        const def = parsed.forDefinitions?.get(unitId);
        return { found: !!def, params: def?.params ?? [], schema: def?.inputSchema };
    }
    const use = parsed.uses?.find((item) => item.id === unitId);
    return { found: !!use, params: use?.params ?? [], schema: use?.inputSchema };
}

const SOURCE_BLOCK_OPENER_RE = /^::(define\[|content\[|meta\[|use\[|import\{)/;
const SOURCE_FOR_HEADER_RE = /^::for\[/;
const SOURCE_FOR_CLOSE_RE = /^::endfor\b/;
const SOURCE_CLOSE_LINE_RE = /^::\s*$/;
const SOURCE_FENCE_RE = /^(`{3,}|~{3,})/;

function readTemplateInputStateFromSource(
    sourceContent: string,
    unitKind: TemplateInputsTarget["unitKind"],
    unitId: string,
    locator?: EditorSerializerTargetLocator,
): { params: string[]; schema?: TemplateInputSchema } | null {
    const lines = sourceContent.split("\n");
    const resolvedLocator = resolveSerializerTargetLocator({
        content: sourceContent,
        unitKind,
        unitId,
        locator,
        fallbackOccurrence: unitKind === "use" ? "first" : "last",
    });
    const candidateLines = resolvedLocator?.startLine == null
        ? []
        : [resolvedLocator.startLine];
    for (const lineIndex of candidateLines) {
        if (!matchesSourceUnitHeader(lines[lineIndex].trim(), unitKind, unitId)) {
            continue;
        }
        const endLine = findSourceUnitEndLine(lines, lineIndex, unitKind);
        const snippet = lines.slice(lineIndex, endLine + 1).join("\n");
        const parsed = parseMagicMd(snippet);
        const state = readParsedTemplateInputState(parsed, unitKind, unitId);
        if (state.found) return { params: state.params, schema: state.schema };

        const body = lines.slice(lineIndex + 1, endLine).join("\n");
        return { params: extractTemplateParams(body) };
    }
    return null;
}

function matchesSourceUnitHeader(
    trimmed: string,
    unitKind: TemplateInputsTarget["unitKind"],
    unitId: string,
): boolean {
    if (unitKind === "content") {
        const match = trimmed.match(/^::content\[([^\]]+)\](?:\{.*\})?\s*$/);
        return match?.[1] === unitId;
    }
    const match = trimmed.match(new RegExp(`^::${unitKind}\\[([^\\]]+)\\]\\{.*\\}\\s*$`));
    return match?.[1] === unitId;
}

function findSourceUnitEndLine(
    lines: readonly string[],
    headerLine: number,
    unitKind: TemplateInputsTarget["unitKind"],
): number {
    let nestingDepth = 0;
    let forDepth = 0;
    let activeFence: { char: "`" | "~"; length: number } | null = null;

    for (let lineIndex = headerLine + 1; lineIndex < lines.length; lineIndex++) {
        const trimmed = lines[lineIndex].trim();

        if (activeFence) {
            if (
                trimmed.length >= activeFence.length &&
                trimmed === activeFence.char.repeat(trimmed.length)
            ) {
                activeFence = null;
            }
            continue;
        }

        const fenceMatch = SOURCE_FENCE_RE.exec(trimmed);
        if (fenceMatch) {
            activeFence = {
                char: fenceMatch[1][0] as "`" | "~",
                length: fenceMatch[1].length,
            };
            continue;
        }

        if (unitKind === "for") {
            if (SOURCE_FOR_HEADER_RE.test(trimmed)) {
                forDepth++;
                continue;
            }
            if (SOURCE_FOR_CLOSE_RE.test(trimmed)) {
                if (forDepth > 0) forDepth--;
                else return lineIndex;
            }
            continue;
        }

        if (SOURCE_BLOCK_OPENER_RE.test(trimmed)) {
            nestingDepth++;
            continue;
        }
        if (SOURCE_FOR_HEADER_RE.test(trimmed)) {
            forDepth++;
            continue;
        }
        if (SOURCE_FOR_CLOSE_RE.test(trimmed)) {
            if (forDepth > 0) forDepth--;
            continue;
        }
        if (SOURCE_CLOSE_LINE_RE.test(trimmed)) {
            if (nestingDepth > 0) nestingDepth--;
            else if (forDepth === 0) return lineIndex;
        }
    }
    return lines.length - 1;
}

function schemaToFields(schema: TemplateInputSchema | undefined, params: readonly string[]): DraftInputField[] {
    const properties = schema?.properties ?? {};
    const names = [
        ...(schema?.order ?? []).filter((name) => properties[name]),
        ...Object.keys(properties).filter((name) => !(schema?.order ?? []).includes(name)),
        ...params.filter((name) => !properties[name]),
    ];
    const required = new Set(schema?.required ?? []);
    return names.map((name) => {
        const prop = properties[name];
        return createDraftInputField({
            name,
            type: prop?.type ?? "string",
            title: prop?.title ?? "",
            description: prop?.description ?? "",
            defaultValue: prop && Object.prototype.hasOwnProperty.call(prop, "default") ? prop.default : undefined,
            enumValue: prop?.enum?.map((value) => String(value)).join(", ") ?? "",
            required: required.has(name),
        });
    });
}

function fieldsToSchema(fields: DraftInputField[]): TemplateInputSchema | undefined {
    const clean = fields
        .map((field) => ({ ...field, name: field.name.trim() }))
        .filter((field) => /^\w[\w-]*$/.test(field.name));
    if (clean.length === 0) return undefined;
    return {
        type: "object",
        order: clean.map((field) => field.name),
        required: clean.filter((field) => field.required).map((field) => field.name),
        properties: Object.fromEntries(
            clean.map((field) => [
                field.name,
                {
                    type: field.type,
                    ...(field.title.trim() ? { title: field.title.trim() } : {}),
                    ...(field.description.trim() ? { description: field.description.trim() } : {}),
                    ...(hasDefaultDraftValue(field.defaultValue) ? { default: normalizeDefaultDraftValue(field.defaultValue, field.type) } : {}),
                    ...(field.enumValue.trim() ? { enum: field.enumValue.split(",").map((item) => item.trim()).filter(Boolean) } : {}),
                },
            ]),
        ),
    };
}

function parseDefault(value: string, type: TemplateInputType): unknown {
    if (type === "number" || type === "integer") {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : value;
    }
    if (type === "boolean") return value === "true";
    if (type === "array" || type === "object") {
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    }
    return value;
}

function hasDefaultDraftValue(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === "string") return value.trim() !== "";
    return true;
}

function normalizeDefaultDraftValue(value: unknown, type: TemplateInputType): unknown {
    if (typeof value !== "string") return value;
    if (/^\$(?:ref|var)\{[^}]+\}$/.test(value.trim())) return value.trim();
    return parseDefault(value, type);
}

function renderDefaultControl(
    field: DraftInputField,
    value: unknown,
    onChange: (value: unknown) => void,
    t: (key: string, params?: Record<string, unknown>) => string,
) {
    const className = "h-7 w-full rounded border border-border bg-background px-2 font-mono text-[11px] focus:outline-none focus:ring-1 focus:ring-primary/50";
    const placeholder = field.type === "array" || field.type === "object"
        ? t("templateInputs.jsonOrReference")
        : t("templateInputs.defaultValue");

    if (field.enumValue.trim()) {
        const values = field.enumValue.split(",").map((item) => item.trim()).filter(Boolean);
        return (
            <select
                className={className}
                value={value == null ? "" : String(value)}
                onChange={(event) => onChange(event.target.value || undefined)}
            >
                <option value="">{t("templateInputs.noDefaultValue")}</option>
                {values.map((item) => (
                    <option key={item} value={item}>{item}</option>
                ))}
            </select>
        );
    }

    if (field.type === "boolean") {
        return (
            <select
                className={className}
                value={value === undefined || value === null ? "" : String(Boolean(value))}
                onChange={(event) => {
                    if (!event.target.value) onChange(undefined);
                    else onChange(event.target.value === "true");
                }}
            >
                <option value="">{t("templateInputs.noDefaultValue")}</option>
                <option value="true">true</option>
                <option value="false">false</option>
            </select>
        );
    }

    if (field.type === "number" || field.type === "integer") {
        return (
            <input
                type="number"
                step={field.type === "integer" ? 1 : "any"}
                className={className}
                value={value == null ? "" : String(value)}
                onChange={(event) => {
                    const raw = event.target.value;
                    if (!raw) {
                        onChange(undefined);
                        return;
                    }
                    const numeric = Number(raw);
                    onChange(Number.isFinite(numeric) ? numeric : raw);
                }}
                placeholder={placeholder}
            />
        );
    }

    if (field.type === "array" || field.type === "object") {
        return (
            <textarea
                className={`${className} h-auto min-h-14 py-1 leading-4`}
                value={value == null ? "" : formatDefaultValue(value)}
                onChange={(event) => {
                    const raw = event.target.value;
                    if (!raw.trim()) {
                        onChange(undefined);
                        return;
                    }
                    try {
                        onChange(JSON.parse(raw));
                    } catch {
                        onChange(raw);
                    }
                }}
                placeholder={placeholder}
            />
        );
    }

    return (
        <input
            type="text"
            className={className}
            value={value == null ? "" : String(value)}
            onChange={(event) => onChange(event.target.value || undefined)}
            placeholder={placeholder}
        />
    );
}

function formatDefaultValue(value: unknown): string {
    if (typeof value === "string") return value;
    try {
        return JSON.stringify(value);
    } catch {
        return String(value ?? "");
    }
}

function buildInputScopeVariables(
    fields: readonly DraftInputField[],
    t: (key: string, params?: Record<string, unknown>) => string,
): ScopeVariable[] {
    const seen = new Set<string>();
    const result: ScopeVariable[] = [];
    for (const field of fields) {
        const name = field.name.trim();
        if (!/^\w[\w-]*$/.test(name) || seen.has(name)) continue;
        seen.add(name);
        const sampleValue = hasDefaultDraftValue(field.defaultValue)
            ? normalizeDefaultDraftValue(field.defaultValue, field.type)
            : undefined;
        result.push({
            displayName: `input/${name}`,
            refTemplate: `$ref{input/${name}}`,
            origin: { type: "custom", directiveName: "template-input" },
            description: field.title.trim() || field.description.trim() || t("template.inputFallback"),
            sampleValue,
            browsable: typeof sampleValue === "object" && sampleValue !== null,
            color: "blue",
        });
    }
    return result;
}

function createDraftInputField(partial: Partial<Omit<DraftInputField, "key">>): DraftInputField {
    draftInputFieldCounter++;
    return {
        key: `template-input-${draftInputFieldCounter}`,
        name: partial.name ?? "",
        type: partial.type ?? "string",
        title: partial.title ?? "",
        description: partial.description ?? "",
        defaultValue: partial.defaultValue,
        enumValue: partial.enumValue ?? "",
        required: partial.required ?? false,
    };
}

function nextInputName(fields: readonly DraftInputField[]): string {
    const used = new Set(fields.map((field) => field.name));
    let index = fields.length + 1;
    while (used.has(`input${index}`)) index++;
    return `input${index}`;
}
